/**
 * The motif's web half (app.css «Transitions»): every screen and block swap
 * runs through `swap`, which wraps the mutation in a View Transition so the
 * browser snapshots the outgoing pixels and plays echo-sink / echo-rise over
 * them — the pseudo-element rules live in web-compat.css («The motif, snapshot
 * side»). Solid unmounts synchronously and offers no exit hook; the snapshot
 * is what a departure animates on.
 *
 * One contract for callers: the browser runs the callback at *capture time* —
 * the next rendering opportunity, not inside the `swap` call — so code after
 * `swap(...)` must not read the state the callback writes. Anything that
 * needs the new state goes inside the callback, after the mutation
 * (`Runtime.openCard` is the precedent). Interaction never waits: the page
 * stays clickable through the transition, and a superseding swap makes the
 * browser skip the older animation while still running both callbacks in
 * order.
 */

import { currentLifetime, isCurrent } from "./lifetime";

export type SwapKind = "screen" | "center" | "sheet" | "pop" | "tab" | "toast";

let seq = 0;

export function swap(kind: SwapKind, fn: () => void): void {
  const lifetime = currentLifetime();
  // Bare in node (vitest), on engines without the API, and under reduced
  // motion — the mutation must never be hostage to the decoration.
  if (
    typeof document === "undefined" ||
    typeof document.startViewTransition !== "function" ||
    (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches)
  ) {
    if (isCurrent(lifetime)) fn();
    return;
  }
  const id = ++seq;
  // Before the capture: the kind attribute is what grants view-transition
  // names and selects the ruleset, and both snapshots must see it.
  document.documentElement.dataset["vt"] = kind;
  const transition = document.startViewTransition(async () => {
    if (isCurrent(lifetime)) fn();
    // solid-router's navigate() commits one microtask later — Solid's
    // startTransition defers through Promise.resolve().then, and with no
    // <Suspense> and no enableScheduling in this app the deferred update is
    // itself synchronous — and a redirect chained in an effect takes one
    // more. Drain a few so the new snapshot sees the settled DOM. Never rAF
    // here: rendering is suppressed while the transition captures.
    await Promise.resolve();
    await Promise.resolve();
    await Promise.resolve();
  });
  void transition.finished
    .catch(() => {})
    .finally(() => {
      // Only if it is still ours: a later swap superseded this one — the
      // browser skipped our animation but ran both callbacks — and the
      // attribute belongs to it now.
      if (seq === id) delete document.documentElement.dataset["vt"];
    });
}
